import React, { useState, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, CheckSquare, Square, ChevronRight, ChevronDown, Save, BookOpen } from 'lucide-react'
import TaxonomySwitcher from '../../components/browse/TaxonomySwitcher'
import { getTaxonomyNodes } from '../../services/api'
import type { TaxonomyNode } from '../../types'

interface TreeNodeProps {
  node: TaxonomyNode
  allNodes: TaxonomyNode[]
  selectedIds: Set<string>
  expandedIds: Set<string>
  onToggleSelect: (nodeId: string) => void
  onToggleExpand: (nodeId: string) => void
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  allNodes,
  selectedIds,
  expandedIds,
  onToggleSelect,
  onToggleExpand,
}) => {
  const children = allNodes.filter((n) => n.parent_id === node.node_id)
  const hasChildren = children.length > 0
  const isExpanded = expandedIds.has(node.node_id)
  const isSelected = selectedIds.has(node.node_id)

  return (
    <div>
      <div
        className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50 transition-colors group"
        style={{ paddingLeft: `${node.level * 20 + 8}px` }}
      >
        {hasChildren ? (
          <button
            onClick={() => onToggleExpand(node.node_id)}
            className="p-0.5 rounded hover:bg-gray-200 transition-colors"
          >
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            ) : (
              <ChevronRight className="w-4 h-4 text-gray-400" />
            )}
          </button>
        ) : (
          <span className="w-5" />
        )}

        <button
          onClick={() => onToggleSelect(node.node_id)}
          className="flex items-center gap-2 flex-1 text-left"
        >
          {isSelected ? (
            <CheckSquare className="w-4 h-4 text-acad-500 flex-shrink-0" />
          ) : (
            <Square className="w-4 h-4 text-gray-300 flex-shrink-0" />
          )}
          <span className="text-sm text-gray-700">{node.label}</span>
          {node.resource_count > 0 && (
            <span className="text-xs text-gray-400 ml-auto opacity-0 group-hover:opacity-100 transition-opacity">
              {node.resource_count} resources
            </span>
          )}
        </button>
      </div>

      {hasChildren && isExpanded && (
        <div>
          {children.map((child) => (
            <TreeNode
              key={child.node_id}
              node={child}
              allNodes={allNodes}
              selectedIds={selectedIds}
              expandedIds={expandedIds}
              onToggleSelect={onToggleSelect}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default function SubjectManagement() {
  const [activeTaxonomy, setActiveTaxonomy] = useState('anzsrc-2020')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle')

  const { data: nodes, isLoading } = useQuery<TaxonomyNode[]>({
    queryKey: ['admin', 'taxonomy-nodes', activeTaxonomy],
    queryFn: () => getTaxonomyNodes(activeTaxonomy),
  })

  const allNodes = nodes ?? []
  const rootNodes = allNodes.filter((n) => !n.parent_id)

  const handleToggleSelect = useCallback((nodeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(nodeId)) {
        next.delete(nodeId)
      } else {
        next.add(nodeId)
      }
      return next
    })
  }, [])

  const handleToggleExpand = useCallback((nodeId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev)
      if (next.has(nodeId)) {
        next.delete(nodeId)
      } else {
        next.add(nodeId)
      }
      return next
    })
  }, [])

  const handleSelectAll = () => {
    setSelectedIds(new Set(allNodes.map((n) => n.node_id)))
  }

  const handleDeselectAll = () => {
    setSelectedIds(new Set())
  }

  const handleSave = async () => {
    setSaveStatus('saving')
    // Mock API call - would use institution-subjects endpoint
    await new Promise((resolve) => setTimeout(resolve, 1000))
    setSaveStatus('saved')
    setTimeout(() => setSaveStatus('idle'), 2000)
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <BookOpen className="w-6 h-6 text-acad-500" />
          Subject Management
        </h1>
        <p className="text-gray-500 mt-1">
          Configure which taxonomy subjects are visible to your institution
        </p>
      </div>

      {/* Taxonomy Selector */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        <TaxonomySwitcher activeTaxonomyId={activeTaxonomy} onSwitch={setActiveTaxonomy} />
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={handleSelectAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                       border border-gray-300 rounded-lg text-gray-700 bg-white
                       hover:bg-gray-50 transition-colors"
          >
            <CheckSquare className="w-4 h-4" />
            Select All
          </button>
          <button
            onClick={handleDeselectAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                       border border-gray-300 rounded-lg text-gray-700 bg-white
                       hover:bg-gray-50 transition-colors"
          >
            <Square className="w-4 h-4" />
            Deselect All
          </button>
          <span className="text-sm text-gray-400">
            {selectedIds.size} of {allNodes.length} subjects selected
          </span>
        </div>
        <button
          onClick={handleSave}
          disabled={saveStatus === 'saving'}
          className="inline-flex items-center gap-2 px-4 py-2 bg-acad-500 text-white rounded-lg
                     text-sm font-medium hover:bg-acad-600 transition-colors disabled:opacity-50"
        >
          {saveStatus === 'saving' ? (
            <>
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              Saving...
            </>
          ) : saveStatus === 'saved' ? (
            <>
              <Check className="w-4 h-4" />
              Saved
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Configuration
            </>
          )}
        </button>
      </div>

      {/* Tree View */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
        {isLoading ? (
          <div className="space-y-2 p-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse" />
                <div
                  className="h-4 bg-gray-200 rounded animate-pulse"
                  style={{ width: `${60 + Math.random() * 30}%` }}
                />
              </div>
            ))}
          </div>
        ) : rootNodes.length === 0 ? (
          <div className="py-12 text-center">
            <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">No taxonomy nodes found for this taxonomy</p>
          </div>
        ) : (
          <div className="max-h-[600px] overflow-y-auto">
            {rootNodes.map((node) => (
              <TreeNode
                key={node.node_id}
                node={node}
                allNodes={allNodes}
                selectedIds={selectedIds}
                expandedIds={expandedIds}
                onToggleSelect={handleToggleSelect}
                onToggleExpand={handleToggleExpand}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
